"use client";

import type { Rating as FranchiseRating } from "@homeinstead/schemas";
import type React from "react";
import type { CareSeekerShallowLeadFormData } from "~care/types/forms/care-seeker-shallow-lead-form";
import type { CareOptionType } from "./form-options";

import { track } from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useActionState, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { submitCareSeekerShallowForm } from "~care/app/actions/submitCareSeekerShallowForm";
import Stepper from "~care/components/app/landing/Stepper/Stepper";
import { Input } from "~care/components/ui/Input/Input";
import { ServiceLeadFormSubmitEvent } from "~care/types/events";
import { identifyLead, trackLeadCreated } from "~care/types/events/lead-events";
import { careSeekerShallowLeadFormSchema } from "~care/types/forms/care-seeker-shallow-lead-form";
import CareOptionStep from "./CareOptionStep";
import CareRecipientStep from "./CareRecipientStep";
import { ContactStep } from "./ContactStep";
import ThankYou from "./ThankYou";

interface CareSeekerLocalLeadFormProps
  extends React.FormHTMLAttributes<HTMLFormElement> {
  currentStep: number;
  handleBack: () => void;
  handleNext: () => void;
  finalStep: number;
  isDebug?: boolean;

  leadSource: "PPC";

  // From sanity
  title: string;
  subtitle: string;
  franchiseId: number;
  location: {
    name: string;
    postalCode: string;
  };
  franchiseRating?: FranchiseRating;
}

const CareSeekerLocalLeadForm = ({
  className,
  currentStep,
  handleBack,
  handleNext,
  finalStep,
  isDebug,
  title,
  subtitle,
  location,
  franchiseId,
  franchiseRating,
  leadSource,
  ...props
}: CareSeekerLocalLeadFormProps) => {
  const methods = useForm<CareSeekerShallowLeadFormData>({
    defaultValues: {
      step1: { leadSource: leadSource, role: "care-seeker" },
      step2: { careFor: "parent" },
      step3: {
        everydayTasks: false,
        memoryCare: false,
        mobilityAssistance: false,
        other: false,
      },
      step4: {
        // These are correctly set until we do DGEN-383
        location: location.postalCode,
        organizationNumber: franchiseId,
        postalCode: location.postalCode,
      },
      step5: {
        consentTerms: false,
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
      },
    },
    mode: "onBlur",
    resolver: zodResolver(careSeekerShallowLeadFormSchema),
    shouldUnregister: false,
  });

  const { register, getValues } = methods;

  const [state, submitFormAction, isPending] = useActionState(
    submitCareSeekerShallowForm,
    null,
  );

  // make sure these only fire once
  const isSuccessTracked = useRef(false);
  const isErrorTracked = useRef(false);
  const isValidationErrorTracked = useRef(false);

  const [
    careFor,
    step3,
    organizationNumber,
    firstName,
    lastName,
    email,
    phone,
  ] = getValues([
    "step2.careFor",
    "step3",
    "step4.organizationNumber",
    "step5.firstName",
    "step5.lastName",
    "step5.email",
    "step5.phone",
  ]);

  const { memoryCare, everydayTasks, mobilityAssistance, other } = step3;
  const careOptions: CareOptionType[] = useMemo(
    () =>
      [
        memoryCare && "memoryCare",
        everydayTasks && "everydayTasks",
        mobilityAssistance && "mobilityAssistance",
        other && "other",
      ].filter(Boolean) as CareOptionType[],
    [memoryCare, everydayTasks, mobilityAssistance, other],
  );

  /**
   * If form has been successfully submitted,
   * handle server side validations,
   * and submit to Segment
   */
  if (state?.ok && !state?.data?.isError) {
    const leadId = state.data?.id;

    // Only track success once
    if (!isSuccessTracked.current && leadId && organizationNumber) {
      isSuccessTracked.current = true;

      if (isDebug) {
        console.log("Successful submission, tracking in Segment");
        console.table([
          {
            Email: email,
            "First Name": firstName,
            "Franchise Number": organizationNumber,
            "Last Name": lastName,
            "Lead ID": leadId,
            Phone: phone,
          },
        ]);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          franchiseNumber: organizationNumber.toString(),
          page_url: window.location.href,
          recipient: careFor,
          result: "success",
          selectedOptions: careOptions,
        }),
      );

      identifyLead({
        email,
        firstName,
        lastName,
        phone,
      });

      // Track lead creation - its own event for consistency with the other platform
      // (otherwise we could add lead and organization to the "service-lead-form-submit")
      trackLeadCreated({
        leadId,
        organizationNumber,
        recipient: careFor,
        selectedOptions: careOptions,
      });
    }

    return <ThankYou firstName={firstName} />;
  } else if (state?.ok && state?.data?.isError) {
    const errors = state?.data?.errors || [];

    const isDuplicateError = errors.some(
      (error: { code: string }) => error.code === "duplicate",
    );

    if (!isErrorTracked.current) {
      isErrorTracked.current = true;
      const errorResult = isDuplicateError ? "duplicate" : "server-error";

      if (isDebug) {
        console.log("Error submission, tracking in Segment: ", errorResult);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          franchiseNumber: organizationNumber.toString(),
          page_url: window.location.href,
          recipient: careFor,
          result: errorResult,
          selectedOptions: careOptions,
        }),
      );
    }

    // We show thank you to duplicates (spam or otherwise) to throw them off, but we don't let
    // them proceed in the process with creating a lead (in SF).
    return <ThankYou firstName={getValues("step5.firstName")} />;
  } else if (state !== null && !state?.ok) {
    // Only track errors if we've actually attempted a submission
    const validationErrors = state?.validationErrors;

    if (!isValidationErrorTracked.current) {
      isValidationErrorTracked.current = true;
      const errorResult = validationErrors
        ? "validation-error"
        : "server-error";

      if (isDebug) {
        console.log("Error submission, tracking in Segment: ", errorResult);
      }

      track(
        EventName.SERVICE_LEAD_FORM_SUBMIT,
        ServiceLeadFormSubmitEvent({
          franchiseNumber: organizationNumber.toString(),
          page_url: window.location.href,
          recipient: careFor,
          result: errorResult,
          selectedOptions: careOptions,
        }),
      );
    }
  }

  /**
   * Otherwise render form
   * Note: At the moment, some Steps are rendered in a Suspense boundary because each Step calls useSearchParams
   * via useSetQueryParam hook. This is a Next.js requirement.
   */
  return (
    <FormProvider {...methods}>
      <Stepper
        className="w-full md:w-[440px]"
        currentStep={currentStep - 1}
        handleBack={handleBack}
        showBackButton={currentStep > 2}
        totalSteps={finalStep - 2}
      />
      <form
        action={submitFormAction}
        className={cn(className, "h-full")}
        {...props}
      >
        {/* Step 1 - hidden field included in FormData */}
        <Input type="hidden" {...register("step1.role")} value="care-seeker" />
        <Input
          type="hidden"
          {...register("step1.leadSource")}
          value={leadSource}
        />
        <input name="step1.formVariant" type="hidden" value="v1" />

        {/**
         * Step 2 - who needs care?
         */}
        <CareRecipientStep
          className={currentStep === 2 ? "flex" : "hidden"}
          franchiseRating={franchiseRating}
          isDebug={isDebug}
          leadSource={leadSource}
          nextStep={handleNext}
          subtitle={subtitle}
          title={title}
        />
        {/**
         * Step 3 - kinds of assistance needed
         */}
        <CareOptionStep
          className={currentStep === 3 ? "flex" : "hidden"}
          franchiseRating={franchiseRating}
          nextStep={handleNext}
        />

        <input
          name="step4.organizationNumber"
          type="hidden"
          value={franchiseId}
        />
        <input
          name="step4.postalCode"
          type="hidden"
          value={location.postalCode}
        />
        <input
          name="step4.location"
          type="hidden"
          value={location.postalCode}
        />

        {/**
         * Step 4 - Contact information
         */}
        <ContactStep
          className={currentStep === 4 ? "flex" : "hidden"}
          isPending={isPending}
        />
      </form>
    </FormProvider>
  );
};

export default CareSeekerLocalLeadForm;
